import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * QuestionKraeplin Component
 * Used for: Kraeplin Test (Speed & Accuracy)
 * Displays ALL sub-columns from a single lembar (sheet) at once.
 * Each question in allQuestions represents one sub-column of numbers.
 */
const QuestionKraeplin = ({
    question,
    answer,
    onAnswer,
    allQuestions = [],
    getAnswer: getAnswerFromParent,
    disabled = false,
    countdown = null,
    timeRemaining = null,
    onNextQuestion = null,
    inkolom = 0,
    changekolom = 0,
    onFinish = null
}) => {
    // Build grid from all questions in this session (= 1 lembar with many sub-columns)
    const columns = (allQuestions.length > 0 ? allQuestions : [question]).map(q => {
        let rawMaster = q.master_data;
        if (typeof rawMaster === 'string') {
            try { rawMaster = JSON.parse(rawMaster); } catch (e) { rawMaster = {}; }
        }

        let numbers = [];
        if (Array.isArray(q.pilihan) && q.pilihan.length > 0) {
            numbers = q.pilihan;
        } else if (typeof q.pilihan === 'object' && q.pilihan !== null && Object.keys(q.pilihan).length > 0) {
            numbers = Object.values(q.pilihan);
        } else if (rawMaster && Array.isArray(rawMaster.numbers) && rawMaster.numbers.length > 0) {
            numbers = rawMaster.numbers;
        } else if (typeof q.pilihan === 'string') {
            try { numbers = JSON.parse(q.pilihan); } catch (e) { numbers = []; }
        }

        if (!Array.isArray(numbers)) numbers = [];

        return {
            id: q.id,
            numbers: numbers.map(n => {
                const val = parseInt(n, 10);
                return isNaN(val) ? 0 : val;
            }),
            lembar: rawMaster?.lembar || q.lembar || q.kolom || null,
            subKolom: rawMaster?.sub_kolom || null,
        };
    });

    const totalColumns = columns.length;
    const lembarNum = columns[0]?.lembar;

    // Answers: stored per question ID → { "col-row": digit }
    const [allAnswers, setAllAnswers] = useState({});
    const [activeCell, setActiveCell] = useState({ col: 0, row: 0 });
    const gridRef = useRef(null);

    // Initialize answers from parent
    useEffect(() => {
        const combined = {};
        columns.forEach((col, colIdx) => {
            const existingAnswer = getAnswerFromParent ? getAnswerFromParent(col.id) : (colIdx === 0 ? answer : null);
            if (existingAnswer && typeof existingAnswer === 'object') {
                Object.entries(existingAnswer).forEach(([key, val]) => {
                    combined[`${colIdx}-${key}`] = val;
                });
            }
        });
        setAllAnswers(combined);
    }, [allQuestions.length]);

    // Ensure header Sisa Waktu starts at inkolom seconds for active column
    useEffect(() => {
        if (countdown && inkolom > 0) {
            countdown.setDuration(inkolom * 1000);
            countdown.start();
        }
    }, [inkolom]);

    // When header countdown reaches 0, auto-advance column and reset countdown to inkolom
    useEffect(() => {
        if (disabled || !inkolom || inkolom <= 0 || !countdown) return;

        if (countdown.duration === 0) {
            if (activeCell.col + 1 < totalColumns) {
                // Auto-advance to next column
                setActiveCell(current => ({ col: current.col + 1, row: 0 }));
                countdown.setDuration(inkolom * 1000);
                countdown.start();
            } else {
                // All columns in this sheet completed!
                if (typeof onFinish === 'function') {
                    onFinish();
                }
            }
        }
    }, [countdown?.duration, activeCell.col, inkolom, totalColumns, disabled, onFinish]);

    // Auto-scroll active column into view
    useEffect(() => {
        if (gridRef.current) {
            const activeColEl = gridRef.current.querySelector(`[data-col-index="${activeCell.col}"]`);
            if (activeColEl) {
                activeColEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }
    }, [activeCell.col]);

    const handleNumberPad = useCallback((digit) => {
        if (disabled || !columns[activeCell.col]) return;

        const colIdx = activeCell.col;
        const rowIdx = activeCell.row;
        const column = columns[colIdx];
        const maxRow = column.numbers.length - 2; // max answer rows = numbers.length - 1

        // Store answer for the specific question
        const colKey = `${colIdx}-${rowIdx}`;

        // Update combined answers
        const newAllAnswers = { ...allAnswers, [colKey]: digit };
        setAllAnswers(newAllAnswers);

        // Build answer for this specific question and save
        const questionAnswers = {};
        Object.entries(newAllAnswers).forEach(([k, v]) => {
            if (k.startsWith(`${colIdx}-`)) {
                const row = k.split('-')[1];
                questionAnswers[row] = v;
            }
        });
        onAnswer(column.id, questionAnswers);

        // Auto-advance to next cell
        if (rowIdx < maxRow) {
            setActiveCell({ col: colIdx, row: rowIdx + 1 });
        } else if (colIdx + 1 < totalColumns) {
            // Reached top of column
            setActiveCell({ col: colIdx + 1, row: 0 });
            if (countdown && inkolom > 0) {
                countdown.setDuration(inkolom * 1000);
                countdown.start();
            }
        }
    }, [activeCell, allAnswers, columns, countdown, inkolom, disabled, onAnswer, totalColumns]);

    // Physical keyboard listener (digits 0-9)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (disabled) return;
            if (/^[0-9]$/.test(e.key)) {
                e.preventDefault();
                handleNumberPad(parseInt(e.key, 10));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [disabled, handleNumberPad]);

    const getAnswerValue = (colIndex, rowIndex) => {
        const key = `${colIndex}-${rowIndex}`;
        return allAnswers[key] !== undefined ? allAnswers[key] : '';
    };

    const isActive = (colIndex, rowIndex) => {
        return activeCell.col === colIndex && activeCell.row === rowIndex;
    };

    const selectCell = (colIndex, rowIndex) => {
        if (disabled) return;
        // In per-column timer mode, restrict selection to active column only (prevent going back or jumping ahead)
        if (inkolom > 0 && colIndex !== activeCell.col) return;
        setActiveCell({ col: colIndex, row: rowIndex });
    };

    // Calculate progress
    const totalAnswerable = columns.reduce((sum, col) => sum + Math.max(0, col.numbers.length - 1), 0);
    const answeredCount = Object.keys(allAnswers).length;

    return (
        <div className="question-kraeplin">
            {/* Header */}
            <div className="card mb-3" style={{ backgroundColor: '#f56409', color: '#fff' }}>
                <div className="card-body py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-0" style={{ fontWeight: 700 }}>
                                Tes Kraeplin {lembarNum ? `— Lembar ${lembarNum}` : ''}
                            </h4>
                            <small>Jumlahkan 2 angka berdekatan, tulis digit terakhir</small>
                        </div>
                        <div className="text-right">
                            <span className="badge badge-light text-dark" style={{ fontSize: '0.9rem', padding: '6px 12px' }}>
                                {totalColumns} Kolom
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className="card mb-3">
                <div className="card-body py-2">
                    <div className="d-flex align-items-center" style={{ gap: '12px' }}>
                        <span className="badge badge-info" style={{ fontSize: '0.9rem', padding: '6px 10px' }}>Contoh</span>
                        <span style={{ fontSize: '1rem' }}>
                            7 + 8 = 15 → tulis <strong>5</strong> &nbsp;|&nbsp; 3 + 4 = 7 → tulis <strong>7</strong>
                        </span>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Grid Display - Left side */}
                <div className="col-md-8">
                    <div className="card">
                        <div className="card-body">
                            <div ref={gridRef} style={{ overflowX: 'auto' }}>
                                <div className="d-flex" style={{ gap: '6px', minWidth: 'max-content', paddingBottom: '8px' }}>
                                    {columns.map((column, colIndex) => {
                                        const isPastLocked = inkolom > 0 && colIndex < activeCell.col;
                                        const isCurrent = activeCell.col === colIndex;
                                        const isFuture = inkolom > 0 && colIndex > activeCell.col;

                                        let borderColor = '#dee2e6';
                                        let bgColor = '#fff';
                                        let opacity = 1;

                                        if (isCurrent) {
                                            borderColor = '#f56409';
                                            bgColor = '#fff5eb';
                                        } else if (isPastLocked) {
                                            borderColor = '#c3e6cb';
                                            bgColor = '#f8fdf9';
                                            opacity = 0.8;
                                        } else if (isFuture) {
                                            opacity = 0.6;
                                        }

                                        return (
                                            <div
                                                key={colIndex}
                                                data-col-index={colIndex}
                                                style={{
                                                    minWidth: '55px',
                                                    border: isCurrent ? '2px solid #f56409' : `1px solid ${borderColor}`,
                                                    borderRadius: '8px',
                                                    padding: '4px',
                                                    backgroundColor: bgColor,
                                                    opacity: opacity,
                                                    transition: 'all 0.2s ease',
                                                }}
                                            >
                                                <div className="text-center mb-1" style={{
                                                    fontSize: '0.65rem',
                                                    fontWeight: 700,
                                                    color: isCurrent ? '#f56409' : (isPastLocked ? '#28a745' : '#adb5bd'),
                                                }}>
                                                    {isPastLocked ? `✓ K${colIndex + 1}` : (isCurrent ? `🔥 K${colIndex + 1}` : `K${colIndex + 1}`)}
                                                </div>

                                                {/* Numbers and Answer cells */}
                                                {column.numbers.map((number, rowIndex) => (
                                                    <div key={rowIndex}>
                                                        {/* Number */}
                                                        <div className="text-center" style={{
                                                            fontSize: '1.2rem',
                                                            fontWeight: 700,
                                                            padding: '1px 0',
                                                            color: '#2c3e50',
                                                            backgroundColor: isPastLocked ? '#eef2f0' : '#f8f9fa',
                                                            borderRadius: '4px',
                                                            marginBottom: '1px',
                                                        }}>
                                                            {number}
                                                        </div>

                                                        {/* Answer cell (except last row) */}
                                                        {rowIndex < column.numbers.length - 1 && (
                                                            <div
                                                                onClick={() => selectCell(colIndex, rowIndex)}
                                                                className="text-center"
                                                                style={{
                                                                    fontSize: '1.1rem',
                                                                    fontWeight: 700,
                                                                    padding: '1px 0',
                                                                    minHeight: '24px',
                                                                    cursor: isPastLocked || isFuture ? 'not-allowed' : 'pointer',
                                                                    backgroundColor: isActive(colIndex, rowIndex) ? '#fde8d0' : (getAnswerValue(colIndex, rowIndex) !== '' ? '#d4edda' : '#fff'),
                                                                    border: isActive(colIndex, rowIndex) ? '2px solid #f56409' : '1px solid #dee2e6',
                                                                    borderRadius: '4px',
                                                                    marginBottom: '1px',
                                                                    color: getAnswerValue(colIndex, rowIndex) !== '' ? '#155724' : '#6c757d',
                                                                    transition: 'all 0.15s ease',
                                                                }}
                                                            >
                                                                {getAnswerValue(colIndex, rowIndex) !== '' ? getAnswerValue(colIndex, rowIndex) : '-'}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}

                                                {/* Column Progress */}
                                                <div className="text-center mt-1" style={{
                                                    fontSize: '0.6rem',
                                                    color: isPastLocked ? '#28a745' : '#6c757d',
                                                }}>
                                                    {isPastLocked ? 'Selesai' : `${Object.keys(allAnswers).filter(k => k.startsWith(`${colIndex}-`)).length}/${column.numbers.length - 1}`}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Number Pad - Right side */}
                <div className="col-md-4">
                    <div className="card" style={{ position: 'sticky', top: '10px' }}>
                        <div className="card-body">
                            <h6 className="card-title text-center mb-3" style={{ fontWeight: 700 }}>
                                Jawaban
                            </h6>

                            {/* Active cell indicator */}
                            {columns[activeCell.col] && activeCell.row < columns[activeCell.col].numbers.length - 1 && (
                                <div className="text-center mb-3 p-2" style={{ backgroundColor: '#fff5eb', borderRadius: '8px', border: '1px solid #fde8d0' }}>
                                    <div style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                                        {lembarNum ? `Lembar ${lembarNum} — ` : ''}Kolom {activeCell.col + 1}, Baris {activeCell.row + 1}
                                    </div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>
                                        {columns[activeCell.col].numbers[activeCell.row]}
                                        <span style={{ color: '#6c757d', margin: '0 4px' }}>+</span>
                                        {columns[activeCell.col].numbers[activeCell.row + 1]}
                                        <span style={{ color: '#6c757d', margin: '0 4px' }}>=</span>
                                        <span style={{ color: '#f56409' }}>?</span>
                                    </div>
                                </div>
                            )}

                            {/* Number pad 0-9 */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((digit) => (
                                    <button
                                        key={digit}
                                        type="button"
                                        onClick={() => handleNumberPad(digit)}
                                        disabled={disabled}
                                        className="btn btn-lg"
                                        style={{
                                            fontSize: '1.5rem',
                                            fontWeight: 800,
                                            minHeight: '55px',
                                            backgroundColor: '#f56409',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            opacity: disabled ? 0.5 : 1,
                                        }}
                                    >
                                        {digit}
                                    </button>
                                ))}
                            </div>
                            <small className="text-muted d-block text-center mt-2" style={{ fontSize: '0.75rem' }}>
                                💡 Tip: Anda juga dapat menekan tombol angka 0-9 pada keyboard fisik.
                            </small>
                        </div>
                    </div>

                    {/* Progress */}
                    <div className="card mt-3">
                        <div className="card-body py-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">Total Progress</small>
                                <strong>{answeredCount} / {totalAnswerable}</strong>
                            </div>
                            <div className="progress mt-1" style={{ height: '6px' }}>
                                <div
                                    className="progress-bar"
                                    style={{
                                        width: `${(answeredCount / Math.max(1, totalAnswerable)) * 100}%`,
                                        backgroundColor: '#f56409',
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuestionKraeplin;
